import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

// ... (KNOWLEDGE_BASE and Logic functions remain exactly the same as your provided code) ...
const KNOWLEDGE_BASE = [
  {
    id: 'greetings',
    keywords: ['hi', 'hello', 'hey', 'greetings', 'morning', 'afternoon', 'start'],
    text: "Hello! 👋 I'm Eda. I can help you understand how Edalyze works, check pricing, or explain our AI accuracy. What's on your mind?",
    suggestions: ["How does it work?", "Pricing", "Is it accurate?"]
  },
  {
    id: 'pricing',
    keywords: ['price', 'cost', 'money', 'pay', 'subscription', 'plan', 'rupee', '₹', 'free', 'trial'],
    text: "We have plans for every student's journey:\n\n✨ **Starter (₹499/mo):** For monthly school tests.\n🔥 **Core (₹999/mo):** Best for Board/JEE prep (Most Popular).\n🏆 **Advanced (₹2,499/mo):** Unlimited papers & priority processing.\n\nAll plans come with a **7-day free trial**! 🛡️",
    suggestions: ["Start Free Trial", "What is included?"]
  },
  {
    id: 'features',
    keywords: ['feature', 'what does it do', 'capabilities', 'scan', 'upload', 'notebook', 'error'],
    text: "Edalyze is more than just a grader. We are a **Forensic Audit** for your exams:\n\n📸 **Auto-Scan:** Grades handwritten papers in seconds.\n🧠 **Error Classification:** Tells you if marks were lost due to 'Silly Mistakes' or 'Concept Gaps'.\n📝 **Paper Gen:** Creates custom practice tests based on your weak areas.",
    suggestions: ["Error Classification", "Paper Generation"]
  },
  {
    id: 'boards',
    keywords: ['board', 'cbse', 'icse', 'jee', 'neet', 'syllabus', 'exam', 'state'],
    text: "We support all major Indian formats:\n\n• **National:** CBSE, ICSE, JEE (Main/Adv), NEET\n• **State Boards:** Maharashtra, Karnataka, Tamil Nadu, and more.\n\nOur AI is calibrated to match the specific marking scheme of YOUR board. 🎯",
    suggestions: ["How accurate is it?"]
  },
  {
    id: 'accuracy',
    keywords: ['accurate', 'trust', 'wrong', 'mistake', 'reliable', 'precision', 'percent'],
    text: "We take trust seriously. Edalyze operates with **97% classification accuracy**. \n\nWe don't hallucinate marks. Our 'Board-Sync™' engine verifies every grade against official topper answer keys and marking rubrics.",
    suggestions: ["See features", "Try for free"]
  },
  {
    id: 'how_it_works',
    keywords: ['how', 'work', 'process', 'step', 'use'],
    text: "It's a simple 3-step loop:\n\n1️⃣ **Scan:** Upload your answer sheet.\n2️⃣ **Diagnose:** We find *why* you lost marks (Concept vs. Careless).\n3️⃣ **Fix:** We generate a custom 'Wrong Answer Notebook' for you to revise.\n\nReady to try?",
    suggestions: ["Start Scanning"]
  }
];

function findBestMatch(input) {
  const lowerInput = input.toLowerCase();
  let bestMatch = null;
  let highestScore = 0;
  KNOWLEDGE_BASE.forEach(topic => {
    let score = 0;
    topic.keywords.forEach(word => { if (lowerInput.includes(word)) score += 1; });
    if (topic.id === 'pricing' && lowerInput.includes('how much')) score += 2;
    if (topic.id === 'features' && lowerInput.includes('what is')) score += 2;
    if (score > highestScore) { highestScore = score; bestMatch = topic; }
  });
  return highestScore > 0 ? bestMatch : null;
}

function ChatMessage({ message, isBot }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-3 ${isBot ? '' : 'flex-row-reverse'}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isBot ? 'bg-primary/10' : 'bg-muted'}`}>
        {isBot ? <Bot className="w-4 h-4 text-primary" /> : <User className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${isBot ? 'bg-card border border-border text-foreground rounded-tl-none' : 'bg-primary text-primary-foreground rounded-tr-none'}`}>
        <p className="whitespace-pre-line">{message}</p>
      </div>
    </motion.div>
  );
}

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [messages, setMessages] = useState([
    { text: "Hi! I'm Eda. Ask me about pricing, accuracy, or how Edalyze improves your grades! 🚀", isBot: true }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState(["Pricing", "How does it work?", "Supported Boards"]);
  const messagesEndRef = useRef(null);

  // Check if it's the user's first time
  useEffect(() => {
    const hasVisited = localStorage.getItem('edalyze_bot_visited');
    if (!hasVisited) {
      setIsFirstVisit(true);
    }
  }, []);

  const handleOpenChat = () => {
    setIsOpen(true);
    setIsFirstVisit(false); // Hide notifications once opened
    localStorage.setItem('edalyze_bot_visited', 'true'); // Save preference
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages, isTyping]);

  const handleSend = (textInput = input) => {
    if (!textInput.trim()) return;
    setMessages(prev => [...prev, { text: textInput, isBot: false }]);
    setInput('');
    setSuggestions([]);
    setIsTyping(true);

    setTimeout(() => {
      const match = findBestMatch(textInput);
      let responseText = "I'm not sure about that yet. Could you try asking about **Pricing**, **Features**, or **How it works**?";
      let nextSuggestions = ["Pricing", "Features", "Contact Support"];
      if (match) {
        responseText = match.text;
        if (match.suggestions) nextSuggestions = match.suggestions;
      }
      setMessages(prev => [...prev, { text: responseText, isBot: true }]);
      setSuggestions(nextSuggestions);
      setIsTyping(false);
    }, 600);
  };

  return (
    <>
      <AnimatePresence>
        {!isOpen && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
            {/* Tooltip Only on First Visit */}
            {isFirstVisit && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="hidden sm:block bg-card border border-border rounded-xl px-4 py-2 shadow-xl"
              >
                <p className="text-xs font-medium">Need help? Ask Eda! ✨</p>
              </motion.div>
            )}

            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              onClick={handleOpenChat}
              className="relative w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
            >
              <MessageCircle className="w-7 h-7" />
              
              {/* Notifications only show if closed AND it's a first visit */}
              {isFirstVisit && (
                <>
                  <span className="absolute -top-1 -right-1 flex h-4 w-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-[10px] font-bold text-white items-center justify-center">1</span>
                  </span>
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-primary"
                    animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </>
              )}
            </motion.button>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            className="fixed bottom-6 right-6 z-50 w-[90vw] sm:w-[380px] h-[600px] max-h-[80vh] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 bg-primary text-primary-foreground flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Eda AI</h3>
                  <p className="text-xs text-primary-foreground/70 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    Online
                  </p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30 scrollbar-thin">
              {messages.map((msg, idx) => (
                <ChatMessage key={idx} message={msg.text} isBot={msg.isBot} />
              ))}
              {isTyping && (
                <div className="flex gap-2 p-2">
                  <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce [animation-delay:0.2s]" />
                  <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce [animation-delay:0.4s]" />
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 && !isTyping && (
              <div className="px-4 pb-2 flex gap-2 overflow-x-auto scrollbar-none">
                {suggestions.map((sugg, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSend(sugg)}
                    className="flex-shrink-0 text-xs bg-primary/5 hover:bg-primary/10 text-primary border border-primary/20 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap"
                  >
                    {sugg}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="p-4 bg-card border-t border-border">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Ask a question..."
                  className="flex-1 bg-muted border-transparent focus:border-primary focus:ring-0 rounded-xl px-4 text-sm"
                />
                <Button onClick={() => handleSend()} size="icon" className="rounded-xl shrink-0">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
